import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import dayjs from 'dayjs';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select';
import { FormField } from '@/components/custom/arito/form/form-field';
import { AritoIcon } from '@/components/custom/arito';
import { DATE_RANGE_OPTIONS } from '@/lib/dateUtil';

interface AritoFormDateRangeDropdownProps {
  fromDateName: string;
  toDateName: string;
  setDates?: (fromDate: string, toDate: string) => void;
}

const AritoFormDateRangeDropdown: React.FC<AritoFormDateRangeDropdownProps> = ({
  fromDateName,
  toDateName,
  setDates
}) => {
  const [open, setOpen] = useState(false);
  const { setValue, watch } = useFormContext();
  const fromValue = watch(fromDateName);
  const toValue = watch(toDateName);
  const storageKey = `${fromDateName}-${toDateName}-date-range`;
  const [savedRange, setSavedRange] = useState<{ from: string; to: string } | null>(null);

  // Load stored dates on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed?.from && parsed?.to) {
          setSavedRange(parsed);
          setValue(fromDateName, parsed.from);
          setValue(toDateName, parsed.to);
          setDates?.(parsed.from, parsed.to);
        }
      }
    } catch (err) {
      console.error('Failed to load saved date range', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce persist to localStorage when values change
  useEffect(() => {
    const timeout = setTimeout(() => {
      try {
        if (fromValue && toValue) {
          const formattedFrom = dayjs(fromValue).format('YYYY-MM-DD');
          const formattedTo = dayjs(toValue).format('YYYY-MM-DD');
          localStorage.setItem(storageKey, JSON.stringify({ from: formattedFrom, to: formattedTo }));
        }
      } catch (err) {
        console.error('Failed to save date range', err);
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [fromValue, toValue, storageKey]);

  // Re-apply saved values if the form resets to empty after mount
  useEffect(() => {
    if (savedRange && !fromValue && !toValue) {
      setValue(fromDateName, savedRange.from);
      setValue(toDateName, savedRange.to);
      setDates?.(savedRange.from, savedRange.to);
    }
  }, [fromValue, toValue, savedRange, setValue, setDates, fromDateName, toDateName]);

  const handleSelectOption = (optionId: string) => {
    const today = dayjs();
    const option = DATE_RANGE_OPTIONS.find(opt => opt.id === optionId);

    if (option) {
      const { from, to } = option.getValue(today);
      setValue(fromDateName, from);
      setValue(toDateName, to);
      setDates?.(from, to);
    }
  };

  return (
    <div className='flex items-center gap-2'>
      <div className='grid flex-1 grid-cols-1 gap-4 md:grid-cols-2'>
        <FormField name={fromDateName} type='date' />
        <FormField name={toDateName} type='date' />
      </div>
      <Select open={open} onOpenChange={setOpen} onValueChange={handleSelectOption}>
        <SelectTrigger
          className='flex h-9 w-9 items-center justify-center rounded-md border border-input bg-background p-0 hover:bg-accent hover:text-accent-foreground [&>svg:last-child]:hidden'
          aria-label='Chọn khoảng thời gian'
          onMouseEnter={() => setOpen(true)}
        >
          <AritoIcon icon={284} />
        </SelectTrigger>
        <SelectContent align='end' className='z-[9999] w-56'>
          {DATE_RANGE_OPTIONS.map(option => (
            <SelectItem key={option.id} value={option.id}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default AritoFormDateRangeDropdown;
